import { useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SCAEnrollmentState } from '#src/__generated__/globalTypes.ts';
import { Checkbox } from '#src/atoms/inputs/Checkbox/index.tsx';
import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  HeadlineL,
  LabelM,
  LabelS,
} from '#src/atoms/typography/index.tsx';
import { IntlDate } from '#src/components/IntlDate/index.tsx';
import { EditableRow } from '#src/components/fiatWallet/CreateFiatWallet/EditableRow/index.tsx';
import { StepWrapper } from '#src/components/fiatWallet/CreateFiatWallet/StepWrapper/index.tsx';
import {
  MANGOPAY_PRIVACY_POLICY,
  MANGOPAY_TERMS,
} from '#src/components/fiatWallet/CreateFiatWallet/externalLinks.ts';
import { CreateFiatWalletSteps } from '#src/components/fiatWallet/CreateFiatWallet/type.ts';
import { useCurrencyOptions } from '#src/components/fiatWallet/CreateFiatWallet/useCurrencyOptions.ts';
import { useIsNotMajor } from '#src/components/fiatWallet/CreateFiatWallet/useIsNotMajor.ts';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useIntlCountries } from '#src/hooks/useIntlCountries.ts';
import type {
  CreateFiatWalletArgs,
  CreateResultType,
} from '#src/hooks/wallets/useCreateOrUpdateFiatWallet.ts';
import { useCreateOrUpdateFiatWallet } from '#src/hooks/wallets/useCreateOrUpdateFiatWallet.ts';
import { fiatWallet, glossary, userAttributes } from '#src/lib/glossary.ts';

const Content = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
  height: 100%;
`;

const CheckboxWrapper = styled.div`
  border-top: 1px solid var(--c-nd-200);
  padding-top: var(--double-unit);
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: var(--c-link);
`;

type DeclarativeFormArgs = Omit<
  CreateFiatWalletArgs,
  'mangopayTermsAndConditionsAccepted' | 'dob'
> & { dob: Date };

type Props = {
  setStep: (step: CreateFiatWalletSteps) => void;
  args: DeclarativeFormArgs;
  onChange: (a: DeclarativeFormArgs) => void;
};

export const ReviewInfoBeforeAddingId = ({
  setStep,
  args,
  onChange: doOnChange,
}: Props) => {
  const {
    flags: { enableMangopaySca = false },
  } = useFeatureFlags();
  const countriesOfResidence = useIntlCountries(true);
  const nationalities = useIntlCountries(true);
  const currencies = useCurrencyOptions();
  const { create } = useCreateOrUpdateFiatWallet();
  const [declarativeFormArgs, setDeclarativeFormArgs] =
    useState<DeclarativeFormArgs>(args);

  const [
    mangopayTermsAndConditionsAccepted,
    setMangopayTermsAndConditionsAccepted,
  ] = useState<boolean>(false);

  const {
    firstName,
    lastName,
    countryOfResidenceCode,
    nationalityCode,
    dob,
    currency,
  } = declarativeFormArgs;

  const submitAndCreateFiatWallet = async (
    onResult: (result: GraphQLResult) => void
  ) => {
    const createResult = await create({
      ...declarativeFormArgs,
      dob,
      mangopayTermsAndConditionsAccepted,
    });
    onResult(createResult);
  };

  const isNotMajor = useIsNotMajor(dob);

  const onChange =
    (key: keyof DeclarativeFormArgs) => (value: string | Date) => {
      setDeclarativeFormArgs((prev: DeclarativeFormArgs) => ({
        ...prev,
        [key]: value,
      }));
      doOnChange({ ...declarativeFormArgs, [key]: value });
    };

  const onSuccess = (result: CreateResultType) => {
    if (
      enableMangopaySca &&
      'currentUser' in result &&
      result.currentUser?.mangopayUser?.scaEnrollmentState ===
        SCAEnrollmentState.SCA_PENDING_CONFIRMATION
    ) {
      setStep(CreateFiatWalletSteps.MANGOPAY_SCA);
    } else {
      setStep(CreateFiatWalletSteps.ACTIVATION_SUCCESS);
    }
  };

  const disabled =
    isNotMajor ||
    firstName === '' ||
    lastName === '' ||
    countryOfResidenceCode === '' ||
    nationalityCode === '' ||
    !currency ||
    !mangopayTermsAndConditionsAccepted;
  return (
    <StyledGraphqlForm
      onSubmit={(variables, onResult) => {
        submitAndCreateFiatWallet(onResult);
      }}
      onSuccess={onSuccess}
      render={(Error, SubmitButton) => (
        <StepWrapper
          setStep={setStep}
          step={CreateFiatWalletSteps.REVIEW_INFORMATION}
          submitButton={
            <SubmitButton size="medium" disabled={disabled}>
              <FormattedMessage {...fiatWallet.activateCashWallet} />
            </SubmitButton>
          }
        >
          <>
            <HeadlineL as="h3">
              <FormattedMessage defaultMessage="Review your information" />
            </HeadlineL>
            <BodyM>
              <FormattedMessage defaultMessage="You're almost done! Please review your information below for accuracy." />
            </BodyM>
            <Content>
              <Vertical gap={0}>
                <EditableRow
                  value={
                    countriesOfResidence.find(
                      c => c.value === countryOfResidenceCode
                    )?.label || ''
                  }
                  label={
                    <BodyM color="var(--c-white)">
                      <FormattedMessage {...glossary.countryOfResidence} />
                    </BodyM>
                  }
                >
                  <NativeSelect
                    values={countriesOfResidence}
                    value={countryOfResidenceCode || ''}
                    name="countryOfResidence"
                    fullWidth
                    onChange={onChange('countryOfResidenceCode')}
                  />
                </EditableRow>
                <EditableRow
                  value={
                    nationalities.find(n => n.value === nationalityCode)
                      ?.label || ''
                  }
                  label={
                    <BodyM color="var(--c-white)">
                      <FormattedMessage {...glossary.nationality} />
                    </BodyM>
                  }
                >
                  <NativeSelect
                    values={nationalities}
                    value={nationalityCode || ''}
                    name="nationality"
                    fullWidth
                    onChange={onChange('nationalityCode')}
                  />
                </EditableRow>
                <EditableRow
                  value={firstName}
                  label={
                    <BodyM color="var(--c-white)">
                      <FormattedMessage {...glossary.firstName} />
                    </BodyM>
                  }
                >
                  <TextField
                    autoFocus
                    name="firstName"
                    defaultValue={firstName}
                    onChange={event =>
                      onChange('firstName')(event.target.value)
                    }
                  />
                </EditableRow>
                <EditableRow
                  value={lastName}
                  label={
                    <BodyM color="var(--c-white)">
                      <FormattedMessage {...glossary.lastName} />
                    </BodyM>
                  }
                >
                  <TextField
                    autoFocus
                    name="lastName"
                    onChange={event => onChange('lastName')(event.target.value)}
                    defaultValue={lastName}
                  />
                </EditableRow>
                <EditableRow
                  value={
                    <FormattedDate
                      value={dob}
                      day="numeric"
                      month="long"
                      year="numeric"
                      timeZone="UTC"
                    />
                  }
                  label={
                    <BodyM color="var(--c-white)">
                      <FormattedMessage {...glossary.dateOfBirth} />
                    </BodyM>
                  }
                >
                  <IntlDate value={dob} onChange={onChange('dob')} />
                </EditableRow>
                {!!isNotMajor && (
                  <LabelM color="var(--c-red-400)">
                    <FormattedMessage {...glossary.notMajorError} />
                  </LabelM>
                )}
                <EditableRow
                  value={currency}
                  label={
                    <BodyM color="var(--c-white)">
                      <FormattedMessage {...userAttributes.fiatCurrency} />
                    </BodyM>
                  }
                >
                  <NativeSelect
                    fullWidth
                    name="currency"
                    values={currencies}
                    value={currency ?? ''}
                    onChange={onChange('currency')}
                  />
                </EditableRow>
                <CheckboxWrapper>
                  <Checkbox
                    name="mangopayTermsAndConditionsAccepted"
                    noPadding
                    checked={mangopayTermsAndConditionsAccepted}
                    onChange={e => {
                      setMangopayTermsAndConditionsAccepted(e.target.checked);
                    }}
                    label={
                      <LabelS color="var(--c-white)">
                        <FormattedMessage
                          defaultMessage="I agree to Mangopay’s <linkTerms>Terms and Conditions</linkTerms> and that my personal data will be processed pursuant to the <linkPrivacy>Privacy Policy</linkPrivacy>."
                          values={{
                            linkTerms: children => (
                              <StyledLink href={MANGOPAY_TERMS} target="_blank">
                                {children}
                              </StyledLink>
                            ),
                            linkPrivacy: children => (
                              <StyledLink
                                href={MANGOPAY_PRIVACY_POLICY}
                                target="_blank"
                              >
                                {children}
                              </StyledLink>
                            ),
                          }}
                        />
                      </LabelS>
                    }
                  />
                </CheckboxWrapper>
              </Vertical>
              <Error />
            </Content>
          </>
        </StepWrapper>
      )}
    />
  );
};
